import {
  useFetchDossierFile,
  useRemoveDossierFile,
  useRenameDossierFile,
  useUploadDossierFile,
} from "@/features/dossiers/hooks/use-dossier-files";
import {
  MAX_DOSSIER_FILE_BYTES,
  type DossierFile,
  type DossierFileSource,
} from "@/features/dossiers/types";
import { downloadBlob, openBlobInTab } from "@/features/utils/blob";
import { useTranslation } from "@/features/utils/i18n";
import { formatFileSize } from "@/features/utils/numbers";
import { useState } from "react";
import { toast } from "sonner";

/** Types the API renders inline (`sendDocument`); anything else is saved instead. */
const VIEWABLE = /^(application\/pdf|image\/(jpeg|png|webp|heic|heif))$/;

/**
 * Everything a file list does, with its toasts — shared by the full module and
 * the button, so a file opens, saves and uploads the same way from both.
 */
export function useDossierFileActions(
  customerId: string,
  source: DossierFileSource,
) {
  const { t } = useTranslation("dossiers");
  const upload = useUploadDossierFile(customerId);
  const remove = useRemoveDossierFile(customerId);
  const rename = useRenameDossierFile(customerId);
  const fetchFile = useFetchDossierFile();
  const [uploading, setUploading] = useState(0);

  const uploadFiles = async (files: File[]) => {
    const accepted = files.filter((file) => {
      if (file.size <= MAX_DOSSIER_FILE_BYTES) return true;
      toast.error(
        t("errors.tooLarge", {
          name: file.name,
          size: formatFileSize(MAX_DOSSIER_FILE_BYTES),
        }),
      );
      return false;
    });
    if (!accepted.length) return;

    setUploading((count) => count + accepted.length);
    // One after the other: a handful of files, and each toast says which one.
    let done = 0;
    for (const file of accepted) {
      try {
        await upload.mutateAsync({ file, source });
        done += 1;
      } catch (error) {
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : t("errors.uploadFailed", { name: file.name }),
        );
      } finally {
        setUploading((count) => count - 1);
      }
    }
    if (done === 1 && accepted.length === 1) {
      toast.success(t("messages.uploaded", { name: accepted[0].name }));
    } else if (done > 0) {
      toast.success(t("messages.uploadedMany", { count: done }));
    }
  };

  const withBlob = async (
    file: DossierFile,
    then: (blob: Blob) => void,
  ): Promise<void> => {
    try {
      then(await fetchFile(file.id));
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t("errors.openFailed"),
      );
    }
  };

  /** Look at it when the browser can show it, else save it. */
  const open = (file: DossierFile) =>
    withBlob(file, (blob) =>
      VIEWABLE.test(file.content_type)
        ? openBlobInTab(blob)
        : downloadBlob(blob, file.filename),
    );

  const download = (file: DossierFile) =>
    withBlob(file, (blob) => downloadBlob(blob, file.filename));

  const renameFile = async (file: DossierFile, filename: string) => {
    const name = filename.trim();
    if (!name || name === file.filename) return true;
    try {
      await rename.mutateAsync({ fileId: file.id, filename: name });
      toast.success(t("messages.renamed"));
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t("errors.renameFailed"),
      );
      return false;
    }
  };

  const removeFile = async (file: DossierFile) => {
    try {
      await remove.mutateAsync(file.id);
      toast.success(t("messages.deleted"));
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t("errors.deleteFailed"),
      );
    }
  };

  return {
    uploadFiles,
    uploading,
    open,
    download,
    rename: renameFile,
    remove: removeFile,
  };
}
